
import React, { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    IconButton, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, CheckCircle as ActivateIcon, Search as SearchIcon, Info as Info } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { fetchData, deleteData, activateData, postData } from '../../../apiService'; // Ensure postData is defined
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import CircularProgress from '@mui/material/CircularProgress';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import AddIcon from '@mui/icons-material/Add';
import CopyrightIcon from '@mui/icons-material/Copyright';
import RefreshIcon from '@mui/icons-material/Refresh';

function PurchaseIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const history = useHistory();
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const { isAuthenticated, companyId, branchId, permissions, token, logout, userId } = useAuth();
    const hasCreateMasterPermission = permissions.includes('Create Transaction');
    const hasEditMasterPermission = permissions.includes('Edit Transaction');
    const hasDeleteMasterPermission = permissions.includes('Delete Transaction');
    const [isLoading, setIsLoading] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [fileData, setFileData] = useState([]);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        fetchtableData();
    }, [isAuthenticated, companyId, branchId, token, logout]);    
  
    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };


    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        setFromDate(selectedDate);
    };

    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        setToDate(selectedDate);
    };
    
    const fetchtableData = async () => {

        setData([]);
        try {
            const response = await fetchData('PurchaseApi', `/PurchaseDetails/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
            if (response.responseCode === '1111') {
                setData(response.response_data);
                setFilteredData(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching Customer Details:', error);
            if (error.response && error.response.status === 401) {
                logout();
                history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
            }
        }

    };

    const handleDelete = async () => {
        deleteData('PurchaseApi', `${deleteItemId}`, token)
            .then((response) => {
                fetchtableData();
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });
    };

    const handleActivate = (itemId) => {
        activateData('PurchaseApi', `${itemId}`, token)
            .then(() => {
                fetchtableData();
            })
            .catch((error) => {
                console.error('Error activating record:', error);
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    const openImportDialog = () => {
        setImportDialogOpen(true);
    };

    const closeImportDialog = () => {
        setImportDialogOpen(false);
        setFileData([]);
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        const reader = new FileReader();


        reader.onload = (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const sheetData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

            const rowsToRead = sheetData.slice(5); // Read from the 5th row

            const formattedData = rowsToRead
                .filter(row => row[2]) // Skip rows where row[2] (BillingDate) is blank
                .map((row) => {
                    let billingDate = row[2];
                    // Convert "DD.MM.YYYY" to ISO 8601 format
                    if (billingDate) {
                        const [day, month, year] = billingDate.split('.');
                        const date = new Date(`${year}-${month}-${day}T00:00:00Z`);
                        billingDate = !isNaN(date) ? date.toISOString() : null;
                    }

                    return {
                        BillingDate: billingDate,
                        BillDoc: row[4] ? String(row[4]) : "", // Ensure BillDoc is a string                        
                        Material: row[5] ? String(row[5]) : "", // Ensure Material is a string
                        Description: row[6] ? String(row[6]) : "", // Ensure Description is a string      
                        CategoryName: row[7] ? String(row[7]) : "", // Ensure Description is a string                    
                        Qty: row[8] ?? 0,
                        BilledQuantity: row[9] ?? 0,
                        PurchaseRate: row[10] ?? 0,
                        MRP: row[11] ?? 0,
                        GSTRate: row[12] ?? 0,
                        HSNCode: row[13] ? String(row[13]) : "",
                    };
                });

            setFileData(formattedData);

        };

        reader.readAsArrayBuffer(file);
    };

    const handleImport = () => {

        setIsLoading(true);

        const CreateData = {
            CompanyId: companyId,
            BranchId: branchId,
            CreatedBy: userId,
            PurchaseImport: fileData
        };

        postData('PurchaseImportApi', CreateData, token)
            .then((response) => {
                fetchtableData();
                setIsLoading(false);
                closeImportDialog();
                console.log('Data posted successfully:', response);
            })
            .catch((error) => {
                console.error('Error posting data:', error);
                setIsLoading(false);
            });
    };

    function formatDate(dateString) {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        const formattedDate = new Date(dateString).toLocaleDateString('en-GB', options);
        return formattedDate;
    }

    return (
        <div className="w-full  mx-auto" style={{ width: '95%', marginTop: '10px' }}>
            <div className='w-full mb-2 flex'>
                <h5 className='block uppercase tracking-wide text-black productHead flex-grow linkcolor' style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif' }}>Purchase </h5>
                <div style={{ marginLeft: '-30px', marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
                    <SearchIcon onClick={toggleSearchInput} style={{ cursor: 'pointer' }} />
                    {showSearchInput && (
                        <input className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={handleSearch}
                            style={{ marginLeft: '10px', width: '250px' }}
                        />
                    )}
                </div>
                <div style={{ marginRight: '2', maxWidth: '150px', marginLeft: '10px' }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber">
                        FromDate
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                        type="date" value={fromDate} onChange={handleFromDateChange}
                        style={{ marginTop: '-10px', shadow: 'appearance-none', border: 'rounded', width: '100%', paddingY: '2', paddingX: '3', color: 'gray-700', leadingTight: true, focus: { color: 'gray-600', shadow: 'outline' } }}
                    />
                </div>
                <div style={{ marginRight: '2', maxWidth: '150px', marginLeft: '10px' }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber">
                        ToDate
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                        type="date" value={toDate} onChange={handleToDateChange}
                        style={{ marginTop: '-10px', shadow: 'appearance-none', border: 'rounded', width: '100%', paddingY: '2', paddingX: '3', color: 'gray-700', leadingTight: true, focus: { color: 'gray-600', shadow: 'outline' } }}
                    />
                </div>
                <Button
                    style={{ color: "black", height: '36px', marginLeft: '10px', marginTop: '20px' }}
                    onClick={fetchtableData}
                    className="linkcolor"
                >
                    <IconButton onClick={fetchtableData}>
                        <RefreshIcon style={{ color: 'darkmagenta' }} />
                    </IconButton>
                    <span style={{ color: 'darkmagenta', fontWeight: 'bolder', marginTop: '-5px' }}>Refresh</span>
                </Button>


                {hasCreateMasterPermission && (
                    <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
                        <Button

                            onClick={openImportDialog}
                            style={{ marginLeft: '8px', color: 'brown', fontWeight: 'bolder' }}
                        >
                            <ImportExportIcon style={{ color: 'brown', fontWeight: 'bolder' }} />IMPORT PURCHASE
                        </Button>
                    </div>
                )}
            </div>

            <div className="watermark">
                <CopyrightIcon /><b style={{ fontFamily: 'Script Bold Italic' }}>EFFTABS SOLUTIONS LLP</b>
            </div>

            <div className='w-full mb-2 flex' style={{ maxHeight: '520px', overflowY: 'auto' }}>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead sx={{ backgroundColor: '#58A5B3' }}>
                            <TableRow>
                                <TableCell sx={{ borderBottom: '1px solid #000000', fontWeight: 'bolder', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '3%' }}>Sl.No</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #000000', fontWeight: 'bolder', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '13%' }}>GRN Date</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #000000', fontWeight: 'bolder', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '10%' }}>GRN Number</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #000000', fontWeight: 'bolder', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '13%' }}>LR Date</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #000000', fontWeight: 'bolder', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '10%' }}>LR Number</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #000000', fontWeight: 'bolder', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '10%', textAlign: 'center' }}>Amount</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #000000', fontWeight: 'bolder', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '10%', textAlign: 'center' }}>GST</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #000000', fontWeight: 'bolder', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '10%', textAlign: 'center' }}>Total</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #000000', fontWeight: 'bolder', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '10%', textAlign: 'center' }}>Total Quantity</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #000000', fontWeight: 'bolder', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '10%', textAlign: 'center' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{ backgroundColor: '#C3E9E4' }}>
                            {filteredData.map((item, index) => (
                                <TableRow key={item.id} className={item.IsActive ? '' : 'inactiveRow'}>
                                    <TableCell sx={{ borderBottom: '1px solid #C0C0C0', height: '25px', padding: '5px', width: '3%', fontSize: '14px' }}>{index + 1}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #C0C0C0', height: '25px', padding: '5px', width: '13%', fontSize: '14px' }}>{formatDate(item.GRNDate)}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #C0C0C0', height: '25px', padding: '5px', width: '10%', fontSize: '14px' }}>{item.GRNNumber}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #C0C0C0', height: '25px', padding: '5px', width: '13%', fontSize: '14px' }}>{formatDate(item.LRDate)}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #C0C0C0', height: '25px', padding: '5px', width: '10%', fontSize: '14px' }}>{item.LRNumber}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #C0C0C0', height: '25px', padding: '5px', width: '10%', textAlign: 'center', fontSize: '14px' }}>{item.Amount ? item.Amount.toFixed(2) : "0.00"}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #C0C0C0', height: '25px', padding: '5px', width: '10%', textAlign: 'center', fontSize: '14px' }}>{item.GSTAmount ? item.GSTAmount.toFixed(2) : "0.00"}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #C0C0C0', height: '25px', padding: '5px', width: '10%', textAlign: 'center', fontSize: '14px' }}>{item.TotalAmount ? item.TotalAmount.toFixed(2) : "0.00"}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #C0C0C0', height: '25px', padding: '5px', width: '10%', textAlign: 'center', fontSize: '14px' }}>{item.TotalQuantity}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #C0C0C0', height: '25px', padding: '5px', textAlign: 'right' }}>

                                        <IconButton component={Link} to={`/PurchaseDetails/${item.Id}`} color="warning">
                                            <Info />
                                        </IconButton>

                                        {hasEditMasterPermission && item.IsActive && (
                                            <IconButton component={Link} to={`/PurchaseEdit/${item.Id}`} color="primary">
                                                <EditIcon />
                                            </IconButton>
                                        )}
                                        {hasDeleteMasterPermission && item.IsActive && (
                                            <IconButton onClick={() => openConfirmDialog(item.Id)} color="error">
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                        {!item.IsActive && (
                                            <IconButton onClick={() => handleActivate(item.Id)} color="success">
                                                <ActivateIcon />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>

            {/* Confirmation Dialog */}

            <Dialog
                open={confirmDialogOpen}
                onClose={closeConfirmDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{"Delete Record?"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete this record?
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <button
                        onClick={closeConfirmDialog}
                        style={{
                            backgroundColor: '#dc3545',
                            color: '#ffffff',
                            border: '1px solid #dc3545',
                            borderRadius: '4px',
                            padding: '8px 16px',
                            cursor: 'pointer',
                            marginRight: '8px',
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleDelete}
                        style={{
                            backgroundColor: '#28a745',
                            color: '#ffffff',
                            border: '1px solid #28a745',
                            borderRadius: '4px',
                            padding: '8px 16px',
                            cursor: 'pointer',
                        }}
                    >
                        Confirm
                    </button>
                </DialogActions>

            </Dialog>

            {/* Import Dialog */}
            <Dialog
                open={importDialogOpen}
                onClose={closeImportDialog}
                aria-labelledby="import-dialog-title"
                aria-describedby="import-dialog-description"
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle id="import-dialog-title">Import Purchase</DialogTitle>
                <DialogContent>
                    <DialogContentText id="import-dialog-description">
                        Select an Excel file to import purchase data. The data will be previewed before importing.
                    </DialogContentText>
                    <input
                        type="file"
                        accept=".xlsx, .xls"
                        onChange={handleFileChange}
                        style={{ marginTop: '16px' }}
                    />
                    {fileData.length > 0 && (
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead sx={{ backgroundColor: '#f3f3f3' }}>
                                    <TableRow>
                                        {/* Add table headers for the preview data */}
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Billing Date</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Bill Doc</TableCell>                                                                                
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Material</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Description</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Category</TableCell>                                        
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Qty</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Billed Quantity</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Purchase Rate</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>MRP</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>GSTRate</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>HSN Code</TableCell>                                        
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {fileData.map((row, index) => (
                                        <TableRow key={index}>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{formatDate(row.BillingDate)}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.BillDoc}</TableCell>                                            
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.Material}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.Description}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.CategoryName}</TableCell>                                            
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.Qty}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.BilledQuantity}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.PurchaseRate}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.MRP}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.GSTRate}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.HSNCode}</TableCell>                                           
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={closeImportDialog} color="primary">Cancel
                    </Button>

                    <Button variant="contained" onClick={handleImport} color="secondary" disabled={isLoading || fileData.length === 0}>
                        {isLoading ? <CircularProgress size={24} color="inherit" /> : "Import"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default PurchaseIndex;



